import { Clock, KeyRound, Waves } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import "./index.css";
import { morphHeldChord, playChord, playLeadTone, playStrum, releaseHeldChord, startHeldChord } from "./lib/audio";
import { CHORD_QUALITIES, NOTE_NAMES, resolveChord, SCALES, type ChordQuality, type NoteName, type ResolvedChord, type ScaleDegree, type ScaleName } from "./lib/chords";

type PlayMode = "play" | "strum" | "lead";

const modeButtons: Array<{ label: string; mode?: PlayMode }> = [
  { label: "Play", mode: "play" },
  { label: "Strum", mode: "strum" },
  { label: "Arp" },
  { label: "Lead", mode: "lead" },
  { label: "Drone" },
  { label: "Drums" },
  { label: "Seq" },
  { label: "Loop" },
];
const joystickPositions = ["flip", "dom7", "jazz", "add9", "sus4", "sweet", "dark", "aug"] as const satisfies readonly ChordQuality[];
const joystickKeyOrder = ["w", "e", "d", "c", "x", "z", "a", "q"] as const;
const joystickButtonLayout = [
  { quality: "aug", arrow: "↖", placement: "col-start-1 row-start-1" },
  { quality: "flip", arrow: "↑", placement: "col-start-2 row-start-1" },
  { quality: "dom7", arrow: "↗", placement: "col-start-3 row-start-1" },
  { quality: "dark", arrow: "←", placement: "col-start-1 row-start-2" },
  { quality: "jazz", arrow: "→", placement: "col-start-3 row-start-2" },
  { quality: "sweet", arrow: "↙", placement: "col-start-1 row-start-3" },
  { quality: "sus4", arrow: "↓", placement: "col-start-2 row-start-3" },
  { quality: "add9", arrow: "↘", placement: "col-start-3 row-start-3" },
] as const satisfies ReadonlyArray<{ quality: ChordQuality; arrow: string; placement: string }>;
const chordKeyMap: Record<string, ScaleDegree> = {
  h: 1,
  u: 2,
  j: 3,
  i: 4,
  k: 5,
  o: 6,
  l: 7,
};
const chordButtonLayout: Array<{ degree: ScaleDegree; keyLabel: string; row: "top" | "bottom"; placement: string }> = [
  { degree: 2, keyLabel: "U", row: "top", placement: "col-start-2 row-start-1" },
  { degree: 4, keyLabel: "I", row: "top", placement: "col-start-4 row-start-1" },
  { degree: 6, keyLabel: "O", row: "top", placement: "col-start-6 row-start-1" },
  { degree: 1, keyLabel: "H", row: "bottom", placement: "col-start-1 row-start-2" },
  { degree: 3, keyLabel: "J", row: "bottom", placement: "col-start-3 row-start-2" },
  { degree: 5, keyLabel: "K", row: "bottom", placement: "col-start-5 row-start-2" },
  { degree: 7, keyLabel: "L", row: "bottom", placement: "col-start-7 row-start-2" },
];
const qualityKeys: Record<string, ChordQuality> = {
  [joystickKeyOrder[0]]: joystickPositions[0],
  [joystickKeyOrder[1]]: joystickPositions[1],
  [joystickKeyOrder[2]]: joystickPositions[2],
  [joystickKeyOrder[3]]: joystickPositions[3],
  [joystickKeyOrder[4]]: joystickPositions[4],
  [joystickKeyOrder[5]]: joystickPositions[5],
  [joystickKeyOrder[6]]: joystickPositions[6],
  [joystickKeyOrder[7]]: joystickPositions[7],
};
const qualityKeyLabels = Object.fromEntries(Object.entries(qualityKeys).map(([key, quality]) => [quality, key.toUpperCase()])) as Record<ChordQuality, string>;
const gamepadDirections: ChordQuality[] = [...joystickPositions];

export function App() {
  const [selectedKey, setSelectedKey] = useState<NoteName>("C");
  const [selectedScale, setSelectedScale] = useState<ScaleName>("major");
  const [selectedDegree, setSelectedDegree] = useState<ScaleDegree>(1);
  const [selectedQuality, setSelectedQuality] = useState<ChordQuality>("base");
  const [selectedOctave, setSelectedOctave] = useState(4);
  const [selectedPlayMode, setSelectedPlayMode] = useState<PlayMode>("play");
  const [lastPlayed, setLastPlayed] = useState("Ready");
  const [minimalMode, setMinimalMode] = useState(true);
  const [heldDegree, setHeldDegree] = useState<ScaleDegree | null>(null);
  const [heldInputKey, setHeldInputKey] = useState<string | null>(null);
  const activeChord = resolveChord({ key: selectedKey, scale: selectedScale, degree: selectedDegree, quality: selectedQuality });

  const triggerChord = useCallback(
    async (degree: ScaleDegree, quality: ChordQuality = selectedQuality) => {
      const chord = resolveChord({ key: selectedKey, scale: selectedScale, degree, quality });
      const rootTone = chord.tones[0];

      setSelectedDegree(degree);

      if (selectedPlayMode === "lead" && rootTone) {
        setLastPlayed(`Lead ${formatTone(rootTone, selectedOctave)}`);
        await playLeadTone(rootTone, selectedOctave);
        return;
      }

      if (selectedPlayMode === "strum") {
        setLastPlayed(`Strumming ${chord.name} at octave ${selectedOctave}`);
        await playStrum(chord.tones, selectedOctave);
        return;
      }

      setLastPlayed(`Playing ${chord.name} at octave ${selectedOctave}`);
      await playChord(chord.tones, selectedOctave);
    },
    [selectedKey, selectedOctave, selectedPlayMode, selectedQuality, selectedScale],
  );

  const pressChord = useCallback(
    async (degree: ScaleDegree, inputKey: string, quality: ChordQuality = selectedQuality) => {
      const chord = resolveChord({ key: selectedKey, scale: selectedScale, degree, quality });

      setSelectedDegree(degree);
      setHeldDegree(degree);
      setHeldInputKey(inputKey);
      setLastPlayed(`Holding ${chord.name} at octave ${selectedOctave}`);
      await startHeldChord(chord.tones, selectedOctave);
    },
    [selectedKey, selectedOctave, selectedQuality, selectedScale],
  );

  const releaseChord = useCallback((inputKey?: string) => {
    if (inputKey && heldInputKey && inputKey !== heldInputKey) {
      return;
    }

    releaseHeldChord();
    setHeldDegree(null);
    setHeldInputKey(null);
    setLastPlayed("Chord released");
  }, [heldInputKey]);

  const applyModifier = useCallback(
    async (quality: ChordQuality) => {
      setSelectedQuality(quality);

      if (selectedPlayMode === "play" && heldDegree) {
        const chord = resolveChord({ key: selectedKey, scale: selectedScale, degree: heldDegree, quality });

        setLastPlayed(`Holding ${chord.name} at octave ${selectedOctave}`);
        await morphHeldChord(chord.tones, selectedOctave);
        return;
      }

      setLastPlayed(quality === "base" ? "Joystick centered" : `${CHORD_QUALITIES[quality].label} selected`);
    },
    [heldDegree, selectedKey, selectedOctave, selectedPlayMode, selectedScale],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (event.repeat) {
        return;
      }

      if (target?.matches("input, select, textarea")) {
        return;
      }

      const chordDegree = chordKeyMap[event.key.toLowerCase()];
      if (chordDegree) {
        event.preventDefault();
        if (selectedPlayMode === "play") {
          void pressChord(chordDegree, event.key.toLowerCase());
          return;
        }

        void triggerChord(chordDegree);
        return;
      }

      const quality = qualityKeys[event.key.toLowerCase()];
      if (quality) {
        event.preventDefault();
        void applyModifier(quality);
        return;
      }

      if (event.key.toLowerCase() === "s") {
        event.preventDefault();
        void applyModifier("base");
        return;
      }

      if (event.code === "Space") {
        event.preventDefault();
        if (selectedPlayMode === "play") {
          void pressChord(selectedDegree, event.code);
          return;
        }

        void triggerChord(selectedDegree);
        return;
      }

      if (event.key === "[" || event.key === "]") {
        event.preventDefault();
        setSelectedKey(current => cycleValue(NOTE_NAMES, current, event.key === "]" ? 1 : -1));
        return;
      }

      if (event.key.toLowerCase() === "r" || event.key.toLowerCase() === "t") {
        event.preventDefault();
        setSelectedScale(current => cycleValue(Object.keys(SCALES) as ScaleName[], current, event.key.toLowerCase() === "t" ? 1 : -1));
        return;
      }

      if (event.key === "-" || event.key === "=") {
        event.preventDefault();
        setSelectedOctave(current => clamp(current + (event.key === "=" ? 1 : -1), 1, 7));
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (target?.matches("input, select, textarea")) {
        return;
      }

      const chordDegree = chordKeyMap[event.key.toLowerCase()];
      if ((chordDegree || event.code === "Space") && selectedPlayMode === "play") {
        event.preventDefault();
        releaseChord(chordDegree ? event.key.toLowerCase() : event.code);
        return;
      }

      if (qualityKeys[event.key.toLowerCase()]) {
        event.preventDefault();
        void applyModifier("base");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    const onBlur = () => releaseChord();

    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [applyModifier, pressChord, releaseChord, selectedDegree, selectedPlayMode, triggerChord]);

  useEffect(() => {
    let animationFrame = 0;
    let lastGamepadQuality: ChordQuality = "base";

    const pollGamepad = () => {
      const gamepad = navigator.getGamepads?.().find(Boolean);
      const nextQuality = gamepad ? qualityFromStick(gamepad.axes[0] ?? 0, gamepad.axes[1] ?? 0) : "base";

      if (nextQuality !== lastGamepadQuality) {
        lastGamepadQuality = nextQuality;
        void applyModifier(nextQuality);
      }

      animationFrame = requestAnimationFrame(pollGamepad);
    };

    animationFrame = requestAnimationFrame(pollGamepad);
    return () => cancelAnimationFrame(animationFrame);
  }, [applyModifier]);

  useEffect(() => {
    if (selectedPlayMode !== "play") {
      releaseHeldChord();
    }
  }, [selectedPlayMode]);

  return (
    <main className="min-h-screen w-full bg-[#0d1117] px-4 py-6 text-slate-100 sm:px-6 lg:px-10">
      <div className="mx-auto mb-5 flex max-w-7xl items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Lowchord</p>
          <h1 className="text-2xl font-black tracking-tight text-white sm:text-3xl">Lowchord Controls</h1>
        </div>
        <button
          className="rounded-full border border-cyan-200/30 bg-cyan-200/10 px-4 py-2 text-sm font-bold text-cyan-100 transition hover:bg-cyan-200 hover:text-slate-950"
          onClick={() => setMinimalMode(current => !current)}
          type="button"
        >
          {minimalMode ? "Full UI" : "Minimal UI"}
        </button>
      </div>

      {minimalMode ? (
        <MinimalDevice
          activeChord={activeChord}
          lastPlayed={lastPlayed}
          selectedDegree={selectedDegree}
          selectedKey={selectedKey}
          selectedOctave={selectedOctave}
          selectedPlayMode={selectedPlayMode}
          selectedQuality={selectedQuality}
          selectedScale={selectedScale}
          setSelectedKey={setSelectedKey}
          setSelectedPlayMode={setSelectedPlayMode}
          applyModifier={applyModifier}
          setSelectedScale={setSelectedScale}
          triggerChord={triggerChord}
        />
      ) : (
      <section className="mx-auto grid max-w-7xl gap-4">
        <div className="grid gap-4 rounded-[1.25rem] border border-white/10 bg-[#101820] p-4 shadow-2xl shadow-black/30 lg:grid-cols-[1.1fr_1fr]">
          <div className="grid gap-4">
            <div className="grid gap-3 rounded-xl border border-white/10 bg-black/25 p-4 sm:grid-cols-[1fr_1fr_160px]">
              <label className="grid gap-2 text-sm font-semibold text-slate-300">
                Key
                <select
                  className="h-11 rounded-lg border border-white/10 bg-slate-950 px-3 text-slate-100 outline-none focus:ring-2 focus:ring-cyan-300/50"
                  onChange={event => setSelectedKey(event.target.value as NoteName)}
                  value={selectedKey}
                >
                  {NOTE_NAMES.map(note => (
                    <option key={note} value={note}>
                      {note}
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid gap-2 text-sm font-semibold text-slate-300">
                Scale
                <select
                  className="h-11 rounded-lg border border-white/10 bg-slate-950 px-3 text-slate-100 outline-none focus:ring-2 focus:ring-cyan-300/50"
                  onChange={event => setSelectedScale(event.target.value as ScaleName)}
                  value={selectedScale}
                >
                  {Object.keys(SCALES).map(scale => (
                    <option key={scale} value={scale}>
                      {titleCase(scale)}
                    </option>
                  ))}
                </select>
              </label>

              <div className="grid gap-2 text-sm font-semibold text-slate-300">
                Octave
                <div className="grid grid-cols-[44px_1fr_44px] gap-2">
                  <button
                    className="grid h-11 place-items-center rounded-lg border border-white/10 bg-slate-950 text-lg font-black text-slate-100 hover:bg-slate-800"
                    onClick={() => setSelectedOctave(current => clamp(current - 1, 1, 7))}
                    type="button"
                  >
                    -
                  </button>
                  <span className="grid h-11 place-items-center rounded-lg border border-white/10 bg-slate-950 text-xl font-black text-cyan-100">
                    {selectedOctave}
                  </span>
                  <button
                    className="grid h-11 place-items-center rounded-lg border border-white/10 bg-slate-950 text-lg font-black text-slate-100 hover:bg-slate-800"
                    onClick={() => setSelectedOctave(current => clamp(current + 1, 1, 7))}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <div className="grid gap-3 rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-4 sm:grid-cols-[150px_1fr]">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-200">Chord</p>
                <p className="mt-1 text-5xl font-black leading-none text-white">{activeChord.name}</p>
              </div>
              <div className="grid content-center gap-2 text-sm text-emerald-100/85">
                <p>
                  Degree {activeChord.degree} · {selectedKey} {titleCase(selectedScale)} · {titleCase(selectedPlayMode)}
                </p>
                <p>{activeChord.tones.map(tone => formatTone(tone, selectedOctave)).join(" - ")}</p>
                <p className="text-xs font-semibold tracking-[0.08em] text-emerald-200/80">{lastPlayed}</p>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/25 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold">Chord Buttons</h2>
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">H U J I K O L</span>
              </div>
              <div className="grid grid-cols-8 gap-3">
                {chordButtonLayout.map(({ degree, keyLabel, row, placement }) => {
                  const chord = resolveChord({ key: selectedKey, scale: selectedScale, degree, quality: selectedQuality });

                  return (
                    <button
                      className={`group min-h-28 rounded-xl border shadow-lg shadow-black/30 transition hover:-translate-y-0.5 hover:bg-cyan-100 hover:shadow-cyan-500/20 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/50 ${
                        selectedDegree === degree ? "border-cyan-300 bg-cyan-100 text-slate-950" : "border-white/10 bg-slate-100 text-slate-950"
                      } ${
                        row === "top" ? "min-h-20 bg-slate-800 text-cyan-100 hover:bg-slate-700" : ""
                      } col-span-2 ${placement}`}
                      key={degree}
                      onClick={() => void triggerChord(degree)}
                      type="button"
                    >
                      <span className="block text-xs font-black uppercase tracking-[0.22em] opacity-60">{keyLabel}</span>
                      <span className="block text-5xl font-black tracking-tight">{degree}</span>
                      <span className="mt-2 block text-sm font-bold tracking-normal text-slate-500 group-hover:text-cyan-700">
                        {chord.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-rows-[auto_1fr]">
            <div className="rounded-xl border border-white/10 bg-black/25 p-4">
              <h2 className="mb-3 text-lg font-bold">Play Mode</h2>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2">
                {modeButtons.map(({ label, mode }) => (
                  <button
                    className={`h-12 rounded-lg border px-3 text-sm font-semibold transition ${
                      mode && selectedPlayMode === mode
                        ? "border-cyan-200 bg-cyan-200 text-slate-950"
                        : mode
                          ? "border-white/10 bg-white/8 hover:bg-white/15"
                          : "cursor-not-allowed border-white/5 bg-white/[0.03] text-slate-500"
                    }`}
                    disabled={!mode}
                    key={label}
                    onClick={() => mode && setSelectedPlayMode(mode)}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/25 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold">Modifier</h2>
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">W E D C X Z A Q</span>
              </div>
              <div className="grid grid-cols-3 grid-rows-3 gap-2">
                {joystickButtonLayout.map(({ quality, arrow, placement }) => (
                  <button
                    className={`min-h-24 rounded-lg border px-2 py-3 text-center transition hover:bg-cyan-200 hover:text-slate-950 ${
                      selectedQuality === quality ? "border-cyan-200 bg-cyan-200 text-slate-950" : "border-white/10 bg-white/10 text-cyan-100"
                    } ${placement}`}
                    key={quality}
                    onClick={() => void applyModifier(quality)}
                    type="button"
                  >
                    <span className="block text-lg leading-none opacity-70">{arrow}</span>
                    <span className="mt-2 block text-sm font-black leading-tight">{CHORD_QUALITIES[quality].label}</span>
                    <span className="mt-1 block text-xs leading-tight opacity-75">{CHORD_QUALITIES[quality].display}</span>
                    <span className="mt-1 block text-[10px] font-bold leading-none opacity-60">{qualityKeyLabels[quality]}</span>
                  </button>
                ))}
                <div className="col-start-2 row-start-2 grid place-items-center">
                  <button
                    className={`grid size-20 place-items-center rounded-full border text-sm font-black shadow-lg shadow-cyan-900/40 transition hover:scale-105 ${
                      selectedQuality === "base" ? "border-cyan-100/60 bg-cyan-200 text-slate-950" : "border-cyan-100/30 bg-slate-950 text-cyan-100"
                    }`}
                    onClick={() => {
                      void applyModifier("base");
                    }}
                    type="button"
                  >
                    Base
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}
    </main>
  );
}

function MinimalDevice({
  applyModifier,
  activeChord,
  lastPlayed,
  selectedDegree,
  selectedKey,
  selectedOctave,
  selectedPlayMode,
  selectedQuality,
  selectedScale,
  setSelectedKey,
  setSelectedPlayMode,
  setSelectedScale,
  triggerChord,
}: {
  applyModifier: (quality: ChordQuality) => Promise<void>;
  activeChord: ResolvedChord;
  lastPlayed: string;
  selectedDegree: ScaleDegree;
  selectedKey: NoteName;
  selectedOctave: number;
  selectedPlayMode: PlayMode;
  selectedQuality: ChordQuality;
  selectedScale: ScaleName;
  setSelectedKey: Dispatch<SetStateAction<NoteName>>;
  setSelectedPlayMode: Dispatch<SetStateAction<PlayMode>>;
  setSelectedScale: Dispatch<SetStateAction<ScaleName>>;
  triggerChord: (degree: ScaleDegree, quality?: ChordQuality) => Promise<void>;
}) {
  return (
    <section className="mx-auto grid min-h-[calc(100vh-8rem)] max-w-7xl place-items-center">
      <div className="w-full max-w-5xl rounded-[2rem] border border-violet-200/50 bg-[#d5bfd0] p-3 shadow-2xl shadow-black/50 ring-4 ring-violet-300/25 sm:p-5">
        <div className="grid gap-4 rounded-[1.5rem] border-4 border-[#221719] bg-[#2a201f] p-2 sm:grid-cols-[250px_1fr] sm:p-3">
          <div className="relative rounded-[1.2rem] bg-[#d8c3d1] p-5 text-[#1d1718]">
            <div className="text-2xl font-black italic tracking-tight">Lowchord</div>

            <div className="mx-auto mt-6 grid w-48 grid-cols-10 gap-1.5">
              {Array.from({ length: 100 }, (_, index) => {
                const x = index % 10;
                const y = Math.floor(index / 10);
                const dx = x - 4.5;
                const dy = y - 4.5;
                const visible = dx * dx + dy * dy < 25;

                return <span className={`size-3 rounded-full ${visible ? "bg-[#120e0f]" : "bg-transparent"}`} key={index} />;
              })}
            </div>

            <div className="relative mx-auto mt-10 size-32">
              {joystickPositions.map((quality, index) => {
                const angle = (index / joystickPositions.length) * Math.PI * 2 - Math.PI / 2;
                const x = Math.cos(angle) * 54;
                const y = Math.sin(angle) * 54;

                return (
                  <button
                    aria-label={CHORD_QUALITIES[quality].label}
                    className={`absolute left-1/2 top-1/2 size-3 rounded-full transition ${
                      selectedQuality === quality ? "bg-violet-600 shadow-[0_0_12px_rgba(124,58,237,0.85)]" : "bg-[#2a201f]"
                    }`}
                    key={quality}
                    onClick={() => void applyModifier(quality)}
                    style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                    title={`${CHORD_QUALITIES[quality].label}: ${CHORD_QUALITIES[quality].display}`}
                    type="button"
                  />
                );
              })}
              <button
                aria-label="Base"
                className={`absolute left-1/2 top-1/2 size-20 rounded-full border-4 border-[#bda9ba] shadow-xl transition ${
                  selectedQuality === "base" ? "bg-[#efe0eb] shadow-violet-700/40" : "bg-[#cdb7c8]"
                }`}
                onClick={() => {
                  void applyModifier("base");
                }}
                style={{ transform: "translate(-50%, -50%)" }}
                title="Base chord"
                type="button"
              />
            </div>

            <div className="mt-7 text-center text-xs font-black tracking-tight">Batch No.</div>
            <div className="mx-auto mt-1 grid size-7 place-items-center rounded-full border-2 border-[#2a201f] text-sm font-black">2</div>
          </div>

          <div className="rounded-[1.1rem] bg-[#201718] p-2">
            <div className="grid grid-cols-[1.05fr_1fr_1fr_1fr] gap-2">
              <button
                className="grid min-h-24 content-center rounded-xl border-2 border-[#15100f] bg-[#2e4317] p-3 text-left shadow-inner"
                onClick={() => void triggerChord(selectedDegree)}
                type="button"
              >
                <span className="text-xs font-black uppercase tracking-[0.2em] text-lime-200/70">
                  {selectedKey} {titleCase(selectedScale)} · Oct {selectedOctave} · {titleCase(selectedPlayMode)}
                </span>
                <span className="mt-1 text-3xl font-black text-lime-100">{activeChord.name}</span>
                <span className="mt-1 truncate text-xs font-semibold text-lime-100/70">{lastPlayed}</span>
              </button>

              <button
                className="grid min-h-24 place-items-center rounded-xl border-2 border-[#15100f] bg-[#7d748a] text-white shadow-lg shadow-black/25 transition hover:brightness-110"
                onClick={() => setSelectedKey(current => cycleValue(NOTE_NAMES, current, 1))}
                title="Key"
                type="button"
              >
                <KeyRound className="size-9" />
              </button>

              <button
                className="grid min-h-24 place-items-center rounded-xl border-2 border-[#15100f] bg-[#f49a10] text-orange-50 shadow-lg shadow-black/25 transition hover:brightness-110"
                onClick={() => setSelectedScale(current => cycleValue(Object.keys(SCALES) as ScaleName[], current, 1))}
                title="Scale"
                type="button"
              >
                <Waves className="size-9" />
              </button>

              <button
                className="grid min-h-24 place-items-center rounded-xl border-2 border-[#15100f] bg-[#b00000] text-red-50 shadow-lg shadow-black/25 transition hover:brightness-110"
                onClick={() => setSelectedPlayMode(cyclePlayMode)}
                title="Mode"
                type="button"
              >
                <span className="grid place-items-center gap-1">
                  <Clock className="size-8" />
                  <span className="text-xs font-black uppercase tracking-[0.16em]">{selectedPlayMode}</span>
                </span>
              </button>
            </div>

            <div className="mt-2 grid grid-cols-4 grid-rows-[minmax(5.5rem,0.75fr)_minmax(10rem,1.25fr)] gap-2">
              <MinimalPad
                className="col-span-2"
                degree={2}
                isActive={selectedDegree === 2}
                keyLabel="U"
                quality={selectedQuality}
                selectedKey={selectedKey}
                selectedScale={selectedScale}
                triggerChord={triggerChord}
              />
              <MinimalPad
                degree={4}
                isActive={selectedDegree === 4}
                keyLabel="I"
                quality={selectedQuality}
                selectedKey={selectedKey}
                selectedScale={selectedScale}
                triggerChord={triggerChord}
              />
              <MinimalPad
                degree={6}
                isActive={selectedDegree === 6}
                keyLabel="O"
                quality={selectedQuality}
                selectedKey={selectedKey}
                selectedScale={selectedScale}
                triggerChord={triggerChord}
              />
              {[1, 3, 5, 7].map(degree => (
                <MinimalPad
                  degree={degree as ScaleDegree}
                  isActive={selectedDegree === degree}
                  key={degree}
                  keyLabel={chordButtonLayout.find(button => button.degree === degree)?.keyLabel ?? ""}
                  quality={selectedQuality}
                  selectedKey={selectedKey}
                  selectedScale={selectedScale}
                  triggerChord={triggerChord}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MinimalPad({
  className = "",
  degree,
  isActive,
  keyLabel,
  quality,
  selectedKey,
  selectedScale,
  triggerChord,
}: {
  className?: string;
  degree: ScaleDegree;
  isActive: boolean;
  keyLabel: string;
  quality: ChordQuality;
  selectedKey: NoteName;
  selectedScale: ScaleName;
  triggerChord: (degree: ScaleDegree, quality?: ChordQuality) => Promise<void>;
}) {
  const chord = resolveChord({ key: selectedKey, scale: selectedScale, degree, quality });

  return (
    <button
      className={`group grid content-between rounded-xl border-2 border-[#15100f] bg-[#cfb7c4] p-3 text-left text-[#211719] shadow-[inset_0_-8px_18px_rgba(60,34,52,0.14)] transition hover:-translate-y-0.5 hover:bg-[#ead4df] ${
        isActive ? "ring-4 ring-violet-400/70" : ""
      } ${className}`}
      onClick={() => void triggerChord(degree)}
      type="button"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-xs font-black uppercase tracking-[0.2em] opacity-55">{keyLabel}</span>
        <span className="text-sm font-black opacity-55">{chord.name}</span>
      </span>
      <span className="text-5xl font-black tracking-tight">{degree}</span>
    </button>
  );
}

function titleCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatTone(tone: { note: NoteName; semitoneOffset: number }, baseOctave: number) {
  return `${tone.note}${baseOctave + Math.floor(tone.semitoneOffset / 12)}`;
}

function cyclePlayMode(current: PlayMode): PlayMode {
  if (current === "play") {
    return "strum";
  }

  if (current === "strum") {
    return "lead";
  }

  return "play";
}

function cycleValue<const T extends string>(values: readonly T[], current: T, direction: 1 | -1) {
  const currentIndex = values.indexOf(current);
  const nextIndex = (currentIndex + direction + values.length) % values.length;
  return values[nextIndex]!;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function qualityFromStick(x: number, y: number): ChordQuality {
  if (Math.hypot(x, y) < 0.45) {
    return "base";
  }

  const angle = Math.atan2(y, x) + Math.PI / 2;
  const normalized = (angle + Math.PI * 2) % (Math.PI * 2);
  const index = Math.round(normalized / (Math.PI / 4)) % gamepadDirections.length;
  return gamepadDirections[index]!;
}

export default App;
